import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getScrapperData, getTechStacks, getCandidatesByTech, getCandidateEmails, sendBulkEmails } from "../api";
import ExcelJS from "exceljs";

// ---------------- CLEAN TEXT FUNCTION ----------------
const cleanText = (text) => {
  if (!text) return "";

  return String(text)
    // remove emojis & special unicode icons
    .replace(
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2011-\u26FF]|[\uFE0F])/g,
      ""
    )
    // remove HTML tags
    .replace(/<[^>]+>/g, "")
    // remove common LinkedIn symbols, bullets, arrows, ticks
    .replace(/&nbsp;|&amp;|•|★|✔️|✔|→|➡|–|—|•/g, " ")
    // collapse extra spaces
    .replace(/\s+/g, " ")
    .trim();
};

const MailerPage = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);
  
  // New state for sender-recipient workflow
  const [senderInfo, setSenderInfo] = useState(null);
  const [recipientsList, setRecipientsList] = useState([]);
  const [senderModalOpen, setSenderModalOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    connection_from: "",
    connection_type: "",
    keywords: "",
    search_by: "",
  });
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [popupRecipients, setPopupRecipients] = useState([]);
  const [attachResumes, setAttachResumes] = useState(false);
  const [profileIdDropdownOpen, setProfileIdDropdownOpen] = useState(false);
  const [profileIdSearch, setProfileIdSearch] = useState("");
  const [selectedProfileIdFilters, setSelectedProfileIdFilters] = useState({});
  const profileIdDropdownRef = useRef(null);
  const [candidateDropdownOpen, setCandidateDropdownOpen] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState("");
  const candidateDropdownRef = useRef(null);

  useEffect(() => {
    // Check if we have sender and recipients from ScrapperPage
    if (location.state?.senderProfileId) {
      setSenderInfo({
        profileId: location.state.senderProfileId,
        email: location.state.senderEmail,
        author: location.state.senderAuthor
      });
      setRecipientsList(location.state.recipients || []);
      console.log("📧 Sender:", location.state.senderEmail);
      console.log("📬 Recipients:", location.state.recipients);
    }
  }, [location.state]);

  const getRowId = (row, idx) => row.unique_post_id || row.profile_id || `row-${idx}`;

  const fetchData = async () => {
    try {
      const res = await getScrapperData(filters);
      setData(res.data || []);
      console.log("Scrapper data:", res.data);
    } catch (err) {
      console.error("Error fetching scrapper data:", err);
    }
  };

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort data based on sortConfig
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Compare values
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [data, sortConfig]);

  const profileIds = React.useMemo(() => {
    const ids = Array.from(new Set(data.map((row) => row.profile_id).filter(Boolean)));
    return ids.sort((a, b) => String(a).localeCompare(String(b)));
  }, [data]);

  const filteredProfileIds = React.useMemo(() => {
    const trimmedSearch = profileIdSearch.trim();
    if (trimmedSearch.includes(',')) {
      const ids = trimmedSearch
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      return profileIds.filter((profileId) => ids.includes(String(profileId)));
    }

    return profileIds.filter((profileId) =>
      String(profileId).toLowerCase().includes(trimmedSearch.toLowerCase())
    );
  }, [profileIds, profileIdSearch]);

  const filteredData = React.useMemo(() => {
    const rowsWithEmail = sortedData.filter(
      (row) => row.email && String(row.email).trim() !== ""
    );

    if (Object.keys(selectedProfileIdFilters).length === 0) {
      return rowsWithEmail;
    }
    return rowsWithEmail.filter((row) => selectedProfileIdFilters[row.profile_id]);
  }, [sortedData, selectedProfileIdFilters]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        profileIdDropdownRef.current &&
        !profileIdDropdownRef.current.contains(event.target) &&
        !(candidateDropdownRef.current && candidateDropdownRef.current.contains(event.target))
      ) {
        setProfileIdDropdownOpen(false);
        setCandidateDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const parseProfileIdSearch = (value) =>
    value
      .split(/[,\s;]+/)
      .map((id) => id.trim())
      .filter(Boolean);

  const handleProfileIdSearchChange = (e) => {
    const value = e.target.value;
    setProfileIdSearch(value);

    const ids = parseProfileIdSearch(value);
    if (ids.length === 0) {
      setSelectedProfileIdFilters({});
      return;
    }

    const next = Object.fromEntries(
      profileIds
        .filter((profileId) => ids.includes(String(profileId)))
        .map((profileId) => [profileId, true])
    );

    setSelectedProfileIdFilters(next);
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  // ---------------- Excel Download Function ----------------
  const downloadExcel = async () => {
    const selectedData = filteredData;
    if (selectedData.length === 0) return alert("No rows available for download!");

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("ScrapperData");

    // Define columns
    const columns = [
      { header: "S.NO", key: "sno", width: 10 },
      { header: "Profile ID", key: "profile_id", width: 14 },
      { header: "Post", key: "post", width: 100 },
      { header: "Designation", key: "Designation", width: 25 },
      { header: "Author", key: "author", width: 20 },
      { header: "Email", key: "email", width: 30 },
      { header: "Is Mail", key: "ISMail", width: 15 },
      { header: "Contact Info", key: "contact_info", width: 25 },
      { header: "Connection Type", key: "connection_type", width: 22 },
      { header: "Profile URL", key: "profile_url", width: 35 },
      { header: "Company", key: "company", width: 25 },
      { header: "Keywords", key: "Keywords", width: 30 },
      { header: "Search By", key: "SearchBy", width: 20 },
      { header: "Unique Post ID", key: "unique_post_id", width: 22 },
      { header: "Connection From", key: "connection_from", width: 22 },
      { header: "Created Date", key: "created_date", width: 25 }
    ];

    worksheet.columns = columns;

    // Format header
    worksheet.getRow(1).eachCell(cell => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF0070C0" }
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };
    });

    // Add data rows
    selectedData.forEach((row, idx) => {
      const cleanPost = cleanText(row.post);

      const newRow = worksheet.addRow({
        sno: idx + 1,
        profile_id: row.profile_id,
        post: cleanPost,
        Designation: row.Designation,
        author: row.author,
        email: row.email,
        ISMail: row.ISMail ? "Yes" : "No",
        contact_info: row.contact_info,
        connection_type: row.connection_type,
        profile_url: row.profile_url,
        company: row.company,
        Keywords: row.Keywords || "",
        SearchBy: row.SearchBy || "",
        unique_post_id: row.unique_post_id,
        connection_from: row.connection_from,
        created_date: row.created_date
          ? new Date(row.created_date).toLocaleString()
          : ""
      });

      // AUTO HEIGHT BASED ON TEXT LENGTH
      const approxLines = Math.ceil(cleanPost.length / 60);
      newRow.height = Math.max(20, approxLines * 15);
    });

    // Format table rows
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      row.eachCell(cell => {
        cell.alignment = { vertical: "top", horizontal: "center", wrapText: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };
      });
    });

    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: columns.length }
    };

    // Download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "scrapper-data.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // ---------------- Send Mail Modal State & Handlers ----------------
  // Old workflow state (tech-to-candidates) - reusing sendModalOpen and isSending from above
  const [techs, setTechs] = useState([]);
  const [selectedTech, setSelectedTech] = useState(null);
  const [techCandidates, setTechCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState({});
  const [candidateTemplateStatus, setCandidateTemplateStatus] = useState({}); // candidateId -> {loaded, loading, candidateEmail, candidateName, templates, message}
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const openSendModal = async () => {
    setSenderModalOpen(false);
    setSendModalOpen(true);
    setPopupRecipients(
      filteredData.map((row) => ({
        name: row.author || row.profile_id || "Unknown",
        email: row.email,
        candidateId: row.profile_id || row.CandidateID || null,
        resumePath: row.resume_path || row.resumePath || row.resumeUrl || null
      }))
    );
    setSelectedTech(null);
    setTechCandidates([]);
    setSelectedCandidateId(null);
    setSelectedCandidateIds([]);
    setSelectedTemplateId(null);
    setCandidateTemplateStatus({});
    try {
      const res = await getTechStacks();
      if (res.success) setTechs(res.data || []);
    } catch (err) {
      console.error('Error loading techs', err);
    }
  };

  const closeSendModal = () => {
    setSendModalOpen(false);
    setSelectedTech(null);
    setTechCandidates([]);
    setSelectedCandidates({});
    setCandidateTemplateStatus({});
    setIsSending(false);
  };

  const handleTechChange = async (techId) => {
    setSelectedTech(techId);
    setTechCandidates([]);
    setSelectedCandidateId(null);
    setSelectedTemplateId(null);
    setCandidateTemplateStatus({});
    if (!techId) return;

    try {
      const res = await getCandidatesByTech(techId);
      if (res.success) setTechCandidates(res.data || []);
    } catch (err) {
      console.error('Error loading candidates for tech', err);
    }
  };

  const fetchCandidateTemplateStatus = async (candidateId) => {
    setCandidateTemplateStatus((prev) => ({
      ...prev,
      [candidateId]: {
        ...(prev[candidateId] || {}),
        loading: true,
        loaded: false,
        message: 'Loading email template...'
      }
    }));

    try {
      const res = await getCandidateEmails(candidateId);
      if (res.success) {
        const candidate = res.data.candidate;
        const templates = res.data.templates || [];
        setCandidateTemplateStatus((prev) => ({
          ...prev,
          [candidateId]: {
            loading: false,
            loaded: true,
            candidateEmail: candidate.email,
            candidateName: candidate.name,
            resumePath: candidate.resume_path || candidate.resumePath || null,
            templates,
            message: templates.length > 0 ? 'Email template loaded successfully' : 'No active email template found for candidate'
          }
        }));
        if (templates.length > 0) {
          setSelectedTemplateId(templates[0].emailId);
        }
      } else {
        setCandidateTemplateStatus((prev) => ({
          ...prev,
          [candidateId]: {
            loading: false,
            loaded: false,
            candidateEmail: null,
            candidateName: null,
            templates: [],
            message: res.message || 'Failed to load email template'
          }
        }));
      }
    } catch (err) {
      console.error('Error loading candidate emails', err);
      setCandidateTemplateStatus((prev) => ({
        ...prev,
        [candidateId]: {
          loading: false,
          loaded: false,
          candidateEmail: null,
          candidateName: null,
          templates: [],
          message: 'Network error while loading candidate email details'
        }
      }));
    }
  };

  const handleCandidateChange = async (candidateId) => {
    setSelectedCandidateId(candidateId);
    setSelectedTemplateId(null);
    if (!candidateId) {
      setCandidateTemplateStatus({});
      return;
    }

    await fetchCandidateTemplateStatus(candidateId);
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
  };

  // ============ SEND EMAIL TO SCRAPED RECIPIENTS ============
  const [quickSendModalOpen, setQuickSendModalOpen] = useState(false);
  const [quickSendSubject, setQuickSendSubject] = useState("");
  const [quickSendBody, setQuickSendBody] = useState("");
  const [quickSendLoading, setQuickSendLoading] = useState(false);

  const openQuickSendModal = () => {
    setQuickSendModalOpen(true);
  };

  const closeQuickSendModal = () => {
    setQuickSendModalOpen(false);
    setQuickSendSubject("");
    setQuickSendBody("");
    setQuickSendLoading(false);
  };

  const handleQuickSendEmails = async () => {
    if (selectedEmails.length === 0) {
      return alert("No selected emails to send");
    }

    if (!quickSendSubject.trim() || !quickSendBody.trim()) {
      return alert("Please provide both subject and email body");
    }

    setQuickSendLoading(true);
    try {
      const recipientsPayload = selectedEmails.map((email) => {
        if (!attachResumes) return email;
        const match = data.find((r) => (r.email || '').toLowerCase() === (email || '').toLowerCase());
        if (match) return { email, candidateId: match.profile_id || match.CandidateID, resumePath: match.resume_path || match.resumeUrl };
        return email;
      });

      const payload = {
        recipients: recipientsPayload,
        subject: quickSendSubject,
        body: quickSendBody
      };

      const res = await sendBulkEmails(payload);
      if (res.success) {
        alert(`✅ Emails sent successfully!\nSent: ${res.data?.sentResults?.length || 0}, Failed: ${res.data?.failedResults?.length || 0}`);
        closeQuickSendModal();
        setSelectedEmails([]);
      } else {
        alert("❌ Failed to send emails: " + (res.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Error sending emails", err);
      alert("❌ Network error sending emails");
    } finally {
      setQuickSendLoading(false);
    }
  };

  // ============ SEND EMAILS FROM SENDER TO RECIPIENTS ============
  const openSendEmailFromSender = () => {
    if (!senderInfo) {
      alert("❌ No sender selected. Please go back to Scraper and select a sender.");
      return;
    }
    if (recipientsList.length === 0) {
      alert("❌ No recipients selected. Please go back to Scraper and select recipients.");
      return;
    }
    setSendModalOpen(false);
    setSenderModalOpen(true);
  };

  const closeSendEmailFromSender = () => {
    setSenderModalOpen(false);
    setEmailSubject("");
    setEmailBody("");
    setIsSending(false);
  };

  const handleSendEmails = async () => {
    const recipientsToUse = (popupRecipients && popupRecipients.length > 0) ? popupRecipients : recipientsList;
    if (!recipientsToUse || recipientsToUse.length === 0) {
      return alert("❌ No recipients available to send emails.");
    }
    if (!selectedTech) {
      return alert("❌ Please select a tech category.");
    }
    if (!selectedCandidateIds || selectedCandidateIds.length === 0) {
      return alert("❌ Please select at least one sender candidate.");
    }
    if (!selectedTemplateId) {
      return alert("❌ Please select an email template.");
    }

    setIsSending(true);
    try {
      // For multiple senders: call API once per sender so each sender account sends to all recipients
      const allRecipients = recipientsToUse.map((r) => {
        if (attachResumes) return { email: r.email, candidateId: r.candidateId, resumePath: r.resumePath };
        return r.email;
      });
      let totalSent = 0;
      let totalFailed = 0;

      for (const senderId of selectedCandidateIds) {
        const templatesForSender = candidateTemplateStatus[senderId]?.templates || [];
        const currentTemplate = templatesForSender.find((t) => t.emailId === selectedTemplateId) || templatesForSender[0];

        if (!currentTemplate) {
          alert(`❌ No template available for sender ${senderId}. Aborting.`);
          setIsSending(false);
          return;
        }

        const payload = {
          senderProfileId: senderId,
          recipients: allRecipients,
          templateId: currentTemplate.emailId,
          subject: currentTemplate.subject,
          body: currentTemplate.body
        };

        // send and collect results
        // eslint-disable-next-line no-await-in-loop
        const res = await sendBulkEmails(payload);
        if (res && res.success) {
          totalSent += (res.data?.sentResults?.length || 0);
          totalFailed += (res.data?.failedResults?.length || 0);
        } else {
          totalFailed += allRecipients.length;
        }
      }

      alert(`✅ Emails send requests completed. Total sent attempts: ${totalSent}, Failed attempts: ${totalFailed}`);
      closeSendModal();
    } catch (err) {
      console.error("Error sending emails", err);
      alert("❌ Network error sending emails");
    } finally {
      setIsSending(false);
    }
  };

  // Old workflow handlers - kept for backward compatibility
  const handleSendEmails_Old = async () => {
    const selectedCandidateIds = Object.keys(selectedCandidates);
    if (selectedCandidateIds.length === 0) return alert('Please select at least one candidate.');
    const invalidCandidates = selectedCandidateIds.filter(
      (candidateId) => !candidateTemplateStatus[candidateId]?.loaded || candidateTemplateStatus[candidateId]?.templates?.length === 0
    );
    if (invalidCandidates.length > 0) {
      return alert('Please wait until email templates are loaded for all selected candidates, or deselect candidates without templates.');
    }

    setIsSending(true);
    try {
      const payload = {
        techId: selectedTech,
        candidateIds: selectedCandidateIds
      };

      const res = await sendBulkEmails(payload);
      if (res.success) {
        alert(`Emails sent successfully!\nSent: ${res.data?.summary?.successCount || 0}, Failed: ${res.data?.summary?.failureCount || 0}`);
        closeSendModal();
      } else {
        alert('Failed to send emails: ' + (res.message || 'Unknown'));
      }
    } catch (err) {
      console.error('Error sending emails', err);
      alert('Network error sending emails');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="scrapper-page-wrapper">

      <div className="scrapper-header-section">
        <h1 className="scrapper-title">📧 Mailer - Send Emails</h1>
        
        {senderInfo && recipientsList.length > 0 && (
          <div style={{ 
            backgroundColor: '#f0fdf4', 
            border: '2px solid #22c55e', 
            borderRadius: '6px', 
            padding: '12px', 
            marginTop: '12px',
            fontSize: '14px'
          }}>
            <div style={{ fontWeight: 'bold', color: '#15803d', marginBottom: '8px' }}>
              ✅ Ready to send emails
            </div>
            <div>
              <strong>📤 FROM:</strong> {senderInfo.author} ({senderInfo.email})
            </div>
            <div>
              <strong>📥 TO ({recipientsList.length} recipients):</strong>
            </div>
            <div style={{ marginLeft: '20px', maxHeight: '100px', overflowY: 'auto' }}>
              {recipientsList.map((r, idx) => (
                <div key={idx} style={{ fontSize: '13px' }}>
                  • {r.author} ({r.email})
                </div>
              ))}
            </div>
            <button 
              onClick={openSendEmailFromSender}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📤 Compose & Send Email
            </button>
          </div>
        )}
      </div>

      {/* Filter Buttons */}
      {!showFilters && (
        <div className="filter-toggle-buttons-container">
          <button 
            className="filter-toggle-show-btn"
            onClick={() => setShowFilters(true)}
            type="button"
          >
            Show Filters
          </button>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <>
          <form onSubmit={handleFilterSubmit} className="scrapper-filter-box">
        <div className="scrapper-filter-group">
          <label className="scrapper-label">From Date</label>
          <input
            type="date"
            name="from"
            value={filters.from}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">To Date</label>
          <input
            type="date"
            name="to"
            value={filters.to}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Profile ID</label>
          <div ref={profileIdDropdownRef} className="msd-container" style={{ minWidth: '220px' }}>
            <button
              type="button"
              className="msd-label"
              onClick={() => setProfileIdDropdownOpen((prev) => !prev)}
              style={{ width: '100%', padding: '8px', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              Profile ID{Object.keys(selectedProfileIdFilters).length > 0 ? ` (${Object.keys(selectedProfileIdFilters).length})` : ''}
              <span className="msd-arrow">{profileIdDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {profileIdDropdownOpen && (
              <div className="msd-dropdown" style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px', zIndex: 1000, maxHeight: '300px', overflowY: 'auto', marginTop: '4px' }}>
                <input
                  className="msd-search"
                  placeholder="Search or paste comma-separated IDs"
                  type="text"
                  value={profileIdSearch}
                  onChange={handleProfileIdSearchChange}
                  style={{ width: '95%', margin: '6px auto', padding: '6px', display: 'block', boxSizing: 'border-box' }}
                />
                <label className="msd-option" style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
                  <input
                    type="checkbox"
                    checked={profileIds.length > 0 && Object.keys(selectedProfileIdFilters).length === profileIds.length}
                    onChange={() => {
                      const next = {};
                      if (Object.keys(selectedProfileIdFilters).length !== profileIds.length) {
                        profileIds.forEach((id) => {
                          next[id] = true;
                        });
                      }
                      setSelectedProfileIdFilters(next);
                    }}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />
                  <strong>All</strong>
                </label>
                {filteredProfileIds.length > 0 ? (
                  filteredProfileIds.map((profileId) => (
                    <label key={profileId} className="msd-option" style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
                      <input
                        type="checkbox"
                        checked={!!selectedProfileIdFilters[profileId]}
                        onChange={() => {
                          setSelectedProfileIdFilters((prev) => {
                            const next = { ...prev };
                            if (next[profileId]) {
                              delete next[profileId];
                            } else {
                              next[profileId] = true;
                            }
                            return next;
                          });
                        }}
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                      />
                      {profileId}
                    </label>
                  ))
                ) : (
                  <div className="msd-no-options" style={{ padding: '12px', textAlign: 'center', color: '#999' }}>No profile IDs found</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Connection From</label>
          <input
            type="text"
            name="connection_from"
            placeholder="e.g., LinkedIn"
            value={filters.connection_from}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Connection Type</label>
          <input
            type="text"
            name="connection_type"
            placeholder="e.g., Direct"
            value={filters.connection_type}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Keywords</label>
          <input
            type="text"
            name="keywords"
            placeholder="e.g., AI, Machine Learning"
            value={filters.keywords}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Search By</label>
          <input
            type="text"
            name="search_by"
            placeholder="e.g., LinkedIn, Google"
            value={filters.search_by}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <button type="submit" className="scrapper-filter-btn">
          Filter
        </button>

        <button className="scrapper-download-btn" onClick={downloadExcel} type="button">
          Download Excel
        </button>
      </form>

      <div className="filter-toggle-buttons-container">
        <button 
          className="filter-toggle-hide-btn"
          onClick={() => setShowFilters(false)}
          type="button"
        >
          Hide Filters
        </button>
      </div>
        </>
      )}

      <div className="scrapper-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: 8 }}>
          {selectedEmails.length > 0 && (
            <button 
              className="scrapper-sendmail-btn" 
              onClick={openQuickSendModal}
              style={{ backgroundColor: '#FF6B6B' }}
              type="button"
            >
              📧 Send to {selectedEmails.length} Selected Emails
            </button>
          )}
          <button className="scrapper-sendmail-btn" onClick={openSendModal} type="button">Send Mail</button>
        </div>
        <table className="scrapper-table">
          <thead>
            <tr>
              {[
                { label: "S.NO", key: "sno" },
                { label: "Profile ID", key: "profile_id", className: "profileid-column" },
                { label: "Post", key: "post", className: "post-column" },
                { label: "Designation", key: "Designation", className: "designation-column" },
                { label: "Author", key: "author" },
                { label: "Email", key: "email", className: "email-column" },
                { label: "IsMail", key: "ISMail", className: "ismail-column" },
                { label: "Contact Info", key: "contact_info" },
                { label: "Connection Type", key: "connection_type", className: "connectiontype-column" },
                { label: "Profile URL", key: "profile_url" },
                { label: "Company", key: "company", className: "company-column" },
                { label: "Keywords", key: "Keywords" },
                { label: "Search By", key: "SearchBy" },
                { label: "Unique Post ID", key: "unique_post_id" },
                { label: "Connection From", key: "connection_from" },
                { label: "Created Date", key: "created_date" },
              ].map((col) => (
                <th 
                  key={col.key} 
                  className={`scrapper-th scrapper-th-sortable-header ${col.className || ''}`}
                  onClick={() => handleSort(col.key)}
                  title="Click to sort"
                >
                  {col.label}
                  {sortConfig.key === col.key ? (
                    <span className="scrapper-sort-arrow">
                      {sortConfig.direction === 'asc' ? '' : ''}
                    </span>
                  ) : (
                    <span className="scrapper-sort-unsorted-arrow"></span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={16} className="scrapper-no-data">
                  No data found.
                </td>
              </tr>
            ) : (
              filteredData.map((row, idx) => {
                const rowId = getRowId(row, idx);
                return (
                  <tr key={`${rowId}-${idx}`} className="scrapper-tr-hover">
                    <td className="scrapper-td scrapper-td-center-bold">
                      {idx + 1}
                    </td>

                    <td className="scrapper-td profileid-column">{row.profile_id}</td>

                  <td className="scrapper-td scrapper-td-post-content post-column">
                    {cleanText(row.post)}
                  </td>

                  <td className="scrapper-td designation-column">{row.Designation}</td>
                  <td className="scrapper-td">{row.author}</td>
                  <td className="scrapper-td email-column">{row.email}</td>

                  <td className="scrapper-td scrapper-td-center ismail-column">
                    {row.ISMail ? "✔️" : ""}
                  </td>

                  <td className="scrapper-td">{row.contact_info}</td>
                  <td className="scrapper-td connectiontype-column">{row.connection_type}</td>

                  <td className="scrapper-td">
                    <a href={row.profile_url} target="_blank" rel="noreferrer" className="scrapper-link">
                      View Profile
                    </a>
                  </td>

                  <td className="scrapper-td">{row.company}</td>
                  <td className="scrapper-td">{row.Keywords || "N/A"}</td>
                  <td className="scrapper-td">{row.SearchBy || "N/A"}</td>
                  <td className="scrapper-td">{row.unique_post_id}</td>
                  <td className="scrapper-td">{row.connection_from}</td>

                  <td className="scrapper-td">
                    {row.created_date ? new Date(row.created_date).toLocaleString() : ""}
                  </td>
                </tr>
              );
            })
            )}
          </tbody>
        </table>
        <div className="filter-toggle-buttons-container" style={{ marginTop: '12px' }}>
          <button
            className="filter-toggle-hide-btn"
            type="button"
            onClick={openSendModal}
            style={{ minWidth: '180px' }}
          >
            Go to Mails
          </button>
        </div>
      </div>

      {/* Send Mail Modal */}
      {sendModalOpen && (
        <div className="mailer-modal-overlay">
          <div className="mailer-modal">
            <div className="mailer-modal-header">
              <h3>Send Mail</h3>
              <button className="modal-close-btn" onClick={closeSendModal}>✕</button>
            </div>

            <div className="mailer-modal-body">
              <div className="mailer-field">
                <label>Recipients</label>
                <div style={{ fontWeight: 'bold', color: '#1d4ed8' }}>{popupRecipients.length} recipient(s)</div>
                <div style={{ marginTop: 8, fontSize: '0.9rem', color: '#334155' }}>
                  Sending to all visible rows with email addresses from the data grid.
                </div>
              </div>

              <div className="mailer-field">
                <label>
                  <input type="checkbox" checked={attachResumes} onChange={(e) => setAttachResumes(e.target.checked)} disabled={isSending} style={{ marginRight: 8 }} />
                  Attach resumes (if available)
                </label>
              </div>

              <div className="mailer-field">
                <label>Tech</label>
                <select value={selectedTech || ''} onChange={(e) => handleTechChange(e.target.value)} disabled={isSending}>
                  <option value="">-- Select Tech --</option>
                  {techs.map((t) => (
                    <option key={t.tech_id || t.id} value={t.tech_id || t.id}>{t.tech_name || t.name}</option>
                  ))}
                </select>
              </div>

              <div className="mailer-field">
                <label>Sender Candidate</label>
                {techCandidates.length === 0 ? (
                  <div className="mailer-empty">Choose a tech to load sender candidates</div>
                ) : (
                  <div ref={candidateDropdownRef} className="msd-container" style={{ minWidth: '220px', position: 'relative' }}>
                    <button
                      type="button"
                      className="msd-label"
                      onClick={() => setCandidateDropdownOpen((prev) => !prev)}
                      style={{ width: '100%', padding: '8px', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      Sender Candidate{selectedCandidateIds.length > 0 ? ` (${selectedCandidateIds.length})` : ''}
                      <span className="msd-arrow">{candidateDropdownOpen ? '▲' : '▼'}</span>
                    </button>

                    {candidateDropdownOpen && (
                      <div
                        className="msd-dropdown"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '6px',
                          zIndex: 1000,
                          maxHeight: '300px',
                          overflowY: 'auto',
                          marginTop: '6px',
                          boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                          padding: '6px'
                        }}
                      >
                        <input
                          className="msd-search"
                          placeholder="Search or paste comma-separated IDs"
                          type="text"
                          value={candidateSearch}
                          onChange={(e) => setCandidateSearch(e.target.value)}
                          style={{ width: '100%', margin: '6px 4px', padding: '8px', display: 'block', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                        />

                        <label
                          className="msd-option"
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCandidateIds.length === techCandidates.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const all = techCandidates.map(c => String(c.CandidateID || c.id));
                                setSelectedCandidateIds(all);
                                setSelectedCandidateId(all[0] || null);
                                all.forEach(cid => fetchCandidateTemplateStatus(cid));
                              } else {
                                setSelectedCandidateIds([]);
                                setSelectedCandidateId(null);
                              }
                            }}
                            style={{ width: 16, height: 16, flexShrink: 0, marginRight: '8px', cursor: 'pointer' }}
                          />
                          <strong>All</strong>
                        </label>

                        {techCandidates
                          .filter((c) => {
                            const q = candidateSearch.trim().toLowerCase();
                            if (!q) return true;
                            return (
                              String(c.CandidateID || c.id).toLowerCase().includes(q) ||
                              (c.CandidateName || c.name || '').toLowerCase().includes(q)
                            );
                          })
                          .map((c) => (
                            <label
                              key={c.CandidateID || c.id}
                              className="msd-option"
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedCandidateIds.includes(String(c.CandidateID || c.id))}
                                onChange={(e) => {
                                  const idStr = String(c.CandidateID || c.id);
                                  setSelectedCandidateIds((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) {
                                      next.add(idStr);
                                      // fetch templates when newly selected
                                      fetchCandidateTemplateStatus(idStr);
                                    } else {
                                      next.delete(idStr);
                                    }
                                    const arr = Array.from(next);
                                    setSelectedCandidateId(arr[0] || null);
                                    return arr;
                                  });
                                }}
                                style={{ width: 16, height: 16, flexShrink: 0, marginRight: '8px', cursor: 'pointer' }}
                              />
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.CandidateName || c.name || c.candidate_name || c.CandidateID}
                              </div>
                            </label>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mailer-field">
                <label>Email Template</label>
                {selectedCandidateId && candidateTemplateStatus[selectedCandidateId]?.templates?.length > 0 ? (
                  <select
                    value={selectedTemplateId || ''}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    disabled={isSending}
                  >
                    <option value="">-- Select Email Template --</option>
                    {candidateTemplateStatus[selectedCandidateId].templates.map((template) => (
                      <option key={template.emailId} value={template.emailId}>
                        {template.email_name || template.subject || `Template ${template.emailId}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="mailer-empty">Select a sender candidate to load email templates</div>
                )}
              </div>

              <div className="mailer-field">
                <label>Sender Account</label>
                <div style={{ padding: '10px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                  <div><strong>Name:</strong> {candidateTemplateStatus[selectedCandidateId]?.candidateName || 'N/A'}</div>
                  <div><strong>Email:</strong> {candidateTemplateStatus[selectedCandidateId]?.candidateEmail || 'N/A'}</div>
                </div>
              </div>
            </div>

            <div className="mailer-modal-footer">
              <button 
                className="scrapper-sendmail-btn" 
                onClick={handleSendEmails}
                disabled={isSending}
              >
                {isSending ? 'Sending...' : 'Send Mail'}
              </button>
              <button 
                className="modal-cancel-btn" 
                onClick={closeSendModal}
                disabled={isSending}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal - FROM Sender TO Recipients */}
      {senderModalOpen && (
        <div className="mailer-modal-overlay">
          <div className="mailer-modal">
            <div className="mailer-modal-header">
              <h3>📤 Send Email from {senderInfo?.author}</h3>
              <button className="modal-close-btn" onClick={closeSendEmailFromSender}>✕</button>
            </div>

            <div className="mailer-modal-body">
              <div className="mailer-field">
                <label style={{ fontWeight: 'bold', color: '#059669' }}>📤 Sender</label>
                <div style={{ padding: '8px', backgroundColor: '#ecfdf5', borderRadius: '4px', color: '#047857' }}>
                  {senderInfo?.author} ({senderInfo?.email})
                </div>
              </div>

              <div className="mailer-field">
                <label style={{ fontWeight: 'bold', color: '#0891b2' }}>📥 Recipients ({recipientsList.length})</label>
                <div style={{ maxHeight: '80px', overflowY: 'auto', padding: '8px', backgroundColor: '#f0f9ff', borderRadius: '4px', fontSize: '13px' }}>
                  {recipientsList.map((r, idx) => (
                    <div key={idx}>
                      ✉️ {r.author} ({r.email})
                    </div>
                  ))}
                </div>
              </div>

              <div className="mailer-field">
                <label>
                  <input type="checkbox" checked={attachResumes} onChange={(e) => setAttachResumes(e.target.checked)} disabled={isSending} style={{ marginRight: 8 }} />
                  Attach resumes (if available)
                </label>
              </div>

              <div className="mailer-field">
                <label>📌 Subject</label>
                <input
                  type="text"
                  placeholder="Email subject line"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  disabled={isSending}
                  className="scrapper-input"
                />
              </div>

              <div className="mailer-field">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label>📝 Email Body (HTML supported)</label>
                  <button
                    type="button"
                    onClick={() => setShowHtmlPreview(!showHtmlPreview)}
                    style={{
                      padding: '6px 12px',
                      background: showHtmlPreview ? '#4CAF50' : '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                    disabled={isSending}
                  >
                    {showHtmlPreview ? '✏️ Edit' : '👁️ Preview'}
                  </button>
                </div>
                
                {showHtmlPreview ? (
                  // HTML Preview Mode
                  <div
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '16px',
                      background: '#f9f9f9',
                      minHeight: '200px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      fontFamily: 'Arial, sans-serif',
                      lineHeight: '1.6'
                    }}
                    dangerouslySetInnerHTML={{ __html: emailBody || '<p><em>No content</em></p>' }}
                  />
                ) : (
                  // HTML Edit Mode
                  <textarea
                    placeholder="Enter email content (HTML supported). Example: <h1>Hello</h1><p>This is a test.</p>"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    disabled={isSending}
                    className="scrapper-input"
                    rows={10}
                    style={{ fontFamily: 'monospace', resize: 'vertical' }}
                  />
                )}
              </div>
            </div>

            <div className="mailer-modal-footer">
              <button 
                className="scrapper-sendmail-btn" 
                onClick={handleSendEmails}
                disabled={isSending || !emailSubject.trim() || !emailBody.trim()}
              >
                {isSending ? '⏳ Sending...' : `📤 Send to ${recipientsList.length} Recipients`}
              </button>
              <button 
                className="modal-cancel-btn" 
                onClick={closeSendEmailFromSender}
                disabled={isSending}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailerPage;

